import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  Coins,
  Building2,
  Users,
  Settings,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { usePlatformStripeConfig } from '@/hooks/usePlatformConfig';
import {
  useLastReconciliationRun,
  useTriggerReconciliation,
} from '@/hooks/usePayReconciliation';
import { Button } from '@/components/ui/Button';

export function AdminDashboard() {
  const { t } = useTranslation();
  const { data: stripeConfig } = usePlatformStripeConfig();
  const { data: lastRun, isLoading: runLoading } = useLastReconciliationRun();
  const triggerReconciliation = useTriggerReconciliation();
  const [manualInvoiceId, setManualInvoiceId] = useState('');
  const [manualClinicId,  setManualClinicId]  = useState('');
  const [manualResult, setManualResult] = useState<string | null>(null);

  const stripeConfigured = stripeConfig?.has_test_secret_key || stripeConfig?.has_live_secret_key;
  const isLiveMode = stripeConfig?.stripe_mode === 'live';

  const adminCards = [
    {
      title: t('admin.dashboard.stripe_title'),
      description: t('admin.dashboard.stripe_desc'),
      icon: CreditCard,
      to: '/admin/stripe',
      status: stripeConfigured ? (
        <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <CheckCircle className="h-3 w-3" />
          {isLiveMode ? t('admin.dashboard.live_mode') : t('admin.dashboard.test_mode')}
        </span>
      ) : (
        <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3" />
          {t('admin.dashboard.not_configured')}
        </span>
      ),
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    },
    {
      title: t('admin.dashboard.billing_title'),
      description: t('admin.dashboard.billing_desc'),
      icon: Coins,
      to: '/admin/billing',
      color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    },
    {
      title: t('admin.dashboard.clinics_title'),
      description: t('admin.dashboard.clinics_desc'),
      icon: Building2,
      to: '/admin/clinics',
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    },
    {
      title: t('admin.dashboard.users_title'),
      description: t('admin.dashboard.users_desc'),
      icon: Users,
      to: '/admin/users',
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    },
    {
      title: t('admin.dashboard.settings_title'),
      description: t('admin.dashboard.settings_desc'),
      icon: Settings,
      to: '/admin/settings',
      color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    },
  ];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('admin.dashboard.title')}
        </h1>
        <p className="mt-2 text-gray-500 dark:text-gray-400">
          {t('admin.dashboard.subtitle')}
        </p>
      </div>

      {/* Quick Actions Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
        {adminCards.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className="group relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
          >
            <div className="flex items-start justify-between">
              <div className={`rounded-lg p-3 ${card.color}`}>
                <card.icon className="h-6 w-6" />
              </div>
              {card.status && <div>{card.status}</div>}
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              {card.title}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {card.description}
            </p>
            <div className="mt-4 flex items-center text-sm font-medium text-amber-600 dark:text-amber-400">
              {t('admin.dashboard.manage')}
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        ))}
      </div>

      {/* System Status */}
      <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('admin.dashboard.system_status')}
        </h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.dashboard.stripe_status')}</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {stripeConfigured ? (
                <span className="text-green-600 dark:text-green-400">
                  {isLiveMode ? t('admin.stripe.live') : t('admin.stripe.test')}
                </span>
              ) : (
                <span className="text-amber-600 dark:text-amber-400">
                  {t('admin.dashboard.not_configured')}
                </span>
              )}
            </p>
          </div>
          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.dashboard.platform')}</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">Clinio v2.0</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.dashboard.database')}</p>
            <p className="mt-1 text-lg font-semibold text-green-600 dark:text-green-400">
              {t('admin.dashboard.connected')}
            </p>
          </div>
        </div>
      </div>

      {/* Pay Reconciliation (PAY-11) */}
      <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Pay Reconciliation
            </h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Daily cron (03:00 UTC) — Stripe ↔ Pay ↔ v2 consistency
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setManualResult(null);
              triggerReconciliation.mutate(
                {},
                {
                  onSuccess: (res) => setManualResult(`Run started: ${res?.data?.run_id ?? '—'}`),
                  onError:   (err) => setManualResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`),
                },
              );
            }}
            disabled={triggerReconciliation.isPending}
            className="shrink-0"
          >
            {triggerReconciliation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Run Now
          </Button>
        </div>

        {/* Last run summary */}
        <div className="mt-4">
          {runLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading last run…
            </div>
          ) : lastRun ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Pass 1 */}
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Pass 1 — Invoice recovery</p>
                {lastRun.pass1_diffs ? (
                  <div className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
                    <p>Examined: <span className="font-semibold">{lastRun.pass1_diffs.examined ?? 0}</span></p>
                    <p>Fixed: <span className="font-semibold text-green-600 dark:text-green-400">{lastRun.pass1_diffs.fixed ?? 0}</span></p>
                    <p>Expired: <span className="font-semibold text-amber-600 dark:text-amber-400">{lastRun.pass1_diffs.expired ?? 0}</span></p>
                  </div>
                ) : <p className="mt-1 text-xs text-gray-400">Not run</p>}
              </div>

              {/* Pass 2 */}
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Pass 2 — Refund check</p>
                {lastRun.pass2_diffs ? (
                  <div className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
                    <p>Examined: <span className="font-semibold">{lastRun.pass2_diffs.examined ?? 0}</span></p>
                    <p>Matched: <span className="font-semibold text-green-600 dark:text-green-400">{lastRun.pass2_diffs.matched ?? 0}</span></p>
                    <p>Drift: <span className={`font-semibold ${(lastRun.pass2_diffs.drift ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>{lastRun.pass2_diffs.drift ?? 0}</span></p>
                  </div>
                ) : <p className="mt-1 text-xs text-gray-400">Not run</p>}
              </div>

              {/* Pass 3 */}
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Pass 3 — Tenant status</p>
                {lastRun.pass3_diffs ? (
                  <div className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
                    <p>Examined: <span className="font-semibold">{lastRun.pass3_diffs.examined ?? 0}</span></p>
                    <p>Updated: <span className="font-semibold">{lastRun.pass3_diffs.updated ?? 0}</span></p>
                    <p>Restricted: <span className={`font-semibold ${(lastRun.pass3_diffs.restricted ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>{lastRun.pass3_diffs.restricted ?? 0}</span></p>
                  </div>
                ) : <p className="mt-1 text-xs text-gray-400">Not run</p>}
              </div>

              {/* Pass 4 */}
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Pass 4 — 7-day drift</p>
                {lastRun.pass4_diff ? (
                  <div className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
                    <p>v2 paid: <span className="font-semibold">{lastRun.pass4_diff.v2_count ?? 0}</span></p>
                    <p>Pay count: <span className="font-semibold">{lastRun.pass4_diff.pay_count ?? 0}</span></p>
                    <p>Diff: <span className={`font-semibold ${(lastRun.pass4_diff.diff ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{lastRun.pass4_diff.diff ?? 0}</span></p>
                  </div>
                ) : <p className="mt-1 text-xs text-gray-400">Not run</p>}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">No reconciliation runs yet.</p>
          )}

          {lastRun && (
            <p className="mt-3 text-xs text-gray-400">
              Last run: {new Date(lastRun.started_at).toLocaleString()}
              {lastRun.finished_at ? ` — finished ${new Date(lastRun.finished_at).toLocaleString()}` : ' (in progress)'}
              {lastRun.errors_jsonb.length > 0 && (
                <span className="ml-2 text-amber-500">⚠ {lastRun.errors_jsonb.length} error(s)</span>
              )}
            </p>
          )}
        </div>

        {/* Manual tools */}
        <div className="mt-5 border-t border-gray-100 pt-5 dark:border-gray-700">
          <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Manual reconciliation tools</p>
          <div className="flex flex-wrap gap-3">
            {/* Sync from Pay — per invoice */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={manualInvoiceId}
                onChange={(e) => setManualInvoiceId(e.target.value)}
                placeholder="Invoice UUID"
                className="h-8 w-64 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder-gray-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (!manualInvoiceId.trim()) return;
                  setManualResult(null);
                  triggerReconciliation.mutate(
                    { invoice_id: manualInvoiceId.trim() },
                    {
                      onSuccess: (res) => {
                        setManualResult(`Sync started: ${res?.data?.run_id ?? '—'}`);
                        setManualInvoiceId('');
                      },
                      onError: (err) => setManualResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`),
                    },
                  );
                }}
                disabled={triggerReconciliation.isPending || !manualInvoiceId.trim()}
              >
                Sync from Pay
              </Button>
            </div>

            {/* Refresh Pay status — per clinic */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={manualClinicId}
                onChange={(e) => setManualClinicId(e.target.value)}
                placeholder="Clinic UUID"
                className="h-8 w-64 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder-gray-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (!manualClinicId.trim()) return;
                  setManualResult(null);
                  triggerReconciliation.mutate(
                    { clinic_id: manualClinicId.trim() },
                    {
                      onSuccess: (res) => {
                        setManualResult(`Refresh started: ${res?.data?.run_id ?? '—'}`);
                        setManualClinicId('');
                      },
                      onError: (err) => setManualResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`),
                    },
                  );
                }}
                disabled={triggerReconciliation.isPending || !manualClinicId.trim()}
              >
                Refresh Pay status
              </Button>
            </div>
          </div>

          {manualResult && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{manualResult}</p>
          )}
        </div>
      </div>
    </div>
  );
}
